import {ApolloQueryResult} from '@apollo/client';
import assert from 'assert';

import {EventConstants} from 'constants/EventConstants';
import FileStates from 'FileStates';
import {SchemaTag} from 'folder-items/FileConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import AppDispatcher from 'lib/Dispatcher';
import {
  COMPLEX_POLYMER_CONTENTS_QUERY,
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ComplexPolymerConnection,
  ComplexPolymerType,
  EntityAssociations,
  ResolvedAttachmentInput,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import * as ComplexPolymerDAO from 'modbio/complex-polymers/ComplexPolymerDAO';
import {
  ComplexPolymerSchemaConfigGraphQL,
  generateEntityAssociationsFromTags,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {
  updateComplexPolymerEditingState,
  updateComplexPolymerVersion,
} from 'modbio/redux/ComplexPolymerReduxActions';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import {TagUpdateObject} from 'schemas/tags/TagUtil';

import {ComplexPolymerContentsQuery} from './__generated/ComplexPolymerContentsQuery';
import {
  transformStructureGraphEdgeForBackend,
  transformStructureGraphNodeForBackend,
} from './drag-and-drop/ComplexPolymerDragAndDropUtil';
import {PolymerStructureGraph} from './PolymerStructureGraphConstants';

// We create a stubbable function which queries for complex polymer structure so that we can mock this out in
// ComplexPolymerActions-test.ts.
export const stubbableFunctions = {
  async requeryComplexPolymer(
    complexPolymerId: string,
    versionId: string
  ): Promise<ApolloQueryResult<ComplexPolymerContentsQuery>> {
    return await getApolloClient().query({
      query: COMPLEX_POLYMER_CONTENTS_QUERY,
      variables: {
        complexPolymerId,
        versionId,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    });
  },
};

export async function createComplexPolymer(
  name: string,
  folderId: string,
  schemaId: string,
  tags: TagUpdateObject,
  complexPolymerTags: Array<SchemaTag>,
  schemaConfig: ComplexPolymerSchemaConfigGraphQL,
  studyIds: Array<string>,
  connectionInputs: Array<ComplexPolymerConnection>,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  offset?: number
): Promise<void> {
  const entityAssociations = generateEntityAssociationsFromTags(complexPolymerTags);
  const {complexPolymerId} = await ComplexPolymerDAO.createComplexPolymer({
    name,
    folderId,
    schemaId,
    tags,
    entityAssociations,
    connectionInputs,
    offset,
    resolvedAttachmentInputs,
    studyIds,
  });
  FileStates.get().openFolderItem(complexPolymerId);
}

async function refetchUpdatedComplexPolymer(
  complexPolymerId: string,
  versionId: string
): Promise<ApolloQueryResult<ComplexPolymerContentsQuery>> {
  return await stubbableFunctions.requeryComplexPolymer(complexPolymerId, versionId);
}

export async function updateComplexPolymer(
  complexPolymerId: string,
  complexPolymerVersionId: string,
  entityIds: Array<string>,
  connectionInputs: Array<ComplexPolymerConnection>,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  entityAssociations: EntityAssociations,
  complexPolymerType: ComplexPolymerType,
  offset?: number,
  structureGraph?: PolymerStructureGraph
): Promise<void> {
  assert(
    offset != null || complexPolymerType !== ComplexPolymerType.OLIGO_DUPLEX,
    'Offset is required for oligo duplexes'
  );
  assert(
    offset == null || complexPolymerType !== ComplexPolymerType.OLIGO_CONJUGATE,
    'Offset is not allowed for oligo conjugates'
  );

  const backendStructureGraph = structureGraph
    ? {
        nodes: transformStructureGraphNodeForBackend(structureGraph.nodes),
        edges: transformStructureGraphEdgeForBackend(structureGraph.edges),
      }
    : null;

  const response = await ComplexPolymerDAO.updateComplexPolymer(complexPolymerId, complexPolymerVersionId, {
    entityIds,
    connectionInputs,
    resolvedAttachmentInputs,
    entityAssociations,
    offset,
    structureGraph: backendStructureGraph,
  });

  getStore().dispatch(updateComplexPolymerVersion(complexPolymerId, response.versionId));
  getStore().dispatch(updateComplexPolymerEditingState(complexPolymerId, false));

  // TODO: Ideally, the response from `ComplexPolymerDAO.updateComplexPolymer` would be an updated complex polymer
  // fragment that we could write directly to the Apollo cache.
  await refetchUpdatedComplexPolymer(complexPolymerId, response.versionId);
  notifications.success(
    `Successfully updated ${COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType]} structure`
  );

  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATED,
    tagData: [{id: complexPolymerId, tags: response.tags}],
  });
}

export async function resetComplexPolymerVersion(
  complexPolymerId: string,
  complexPolymerVersionId: string,
  currentVersionId: string
): Promise<void> {
  const response = await ComplexPolymerDAO.resetComplexPolymerVersion(
    complexPolymerId,
    complexPolymerVersionId,
    currentVersionId
  );
  getStore().dispatch(updateComplexPolymerVersion(response.complexPolymerId, response.versionId));

  await refetchUpdatedComplexPolymer(complexPolymerId, response.versionId);
  notifications.success('Successfully reset version');

  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATED,
    tagData: [{id: complexPolymerId, tags: response.tags}],
  });
}
